import React, { useState, useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Scroll from "react-scroll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashCan,
  faTachometer,
  faVideo,
  faArrowLeft,
  faEye,
  faPencil,
  faGears,
  faArrowUpRightFromSquare,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { useRecoilState } from "recoil";

import { CMS_VIDEO_STATE_OPTIONS } from "../../Constants/FieldOptions";
import { getCMSVideoDetailAPI, deleteCMSVideoAPI } from "../../API/CMSVideo";
import FormErrorBox from "../Reusable/FormErrorBox";
import FormInputField from "../Reusable/FormInputField";

import FormSelectField from "../Reusable/FormSelectField";
import { topAlertMessageState, topAlertStatusState } from "../../AppState";
import PageLoadingContent from "../Reusable/PageLoadingContent";

function VideoDetail() {
  ////
  //// URL Parameters.
  ////

  const { id } = useParams();

  ////
  //// Global state.
  ////

  const [topAlertMessage, setTopAlertMessage] =
    useRecoilState(topAlertMessageState);
  const [topAlertStatus, setTopAlertStatus] =
    useRecoilState(topAlertStatusState);

  ////
  //// Component states.
  ////

  const [errors, setErrors] = useState({});
  const [isFetching, setFetching] = useState(false);
  const [forceURL, setForceURL] = useState("");
  const [video, setVideo] = useState(null);
  const [showDeletionConfirmationModal, setShowDeletionConfirmationModal] =
    useState(false);

  ////
  //// Event handling.
  ////

  const fetchDetail = (e) => {
    setFetching(true);
    setErrors({});
    getCMSVideoDetailAPI(
      id,
      onVideoDetailSuccess,
      onVideoDetailError,
      onVideoDetailDone
    );
  };

  const onDeleteConfirmButtonClick = () => {
    console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

    setFetching(true);
    setErrors({});

    deleteCMSVideoAPI(
      id,
      onVideoDeleteSuccess,
      onVideoDeleteError,
      onVideoDeleteDone
    );
    setShowDeletionConfirmationModal(false);
  };

  ////
  //// API.
  ////

  // --- Detail --- //

  function onVideoDetailSuccess(response) {
    console.log("onVideoDetailSuccess: Starting...");
    setVideo(response);
  }

  function onVideoDetailError(apiErr) {
    console.log("onVideoDetailError: Starting...");
    setErrors(apiErr);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onVideoDetailDone() {
    console.log("onVideoDetailDone: Starting...");
    setFetching(false);
  }

  // --- Delete --- //

  function onVideoDeleteSuccess(response) {
    console.log("onVideoDeleteSuccess: Starting..."); // For debugging purposes only.

    // Update notification.
    setTopAlertStatus("success");
    setTopAlertMessage("Video deleted");
    setTimeout(() => {
      console.log(
        "onDeleteConfirmButtonClick: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // Redirect the user to a new page.
    setForceURL("/cms-videos");
  }

  function onVideoDeleteError(apiErr) {
    console.log("onVideoDeleteError: Starting..."); // For debugging purposes only.
    setErrors(apiErr);

    // Update notification.
    setTopAlertStatus("danger");
    setTopAlertMessage("Failed deleting");
    setTimeout(() => {
      console.log(
        "onVideoDeleteError: topAlertMessage, topAlertStatus:",
        topAlertMessage,
        topAlertStatus
      );
      setTopAlertMessage("");
    }, 2000);

    // The following code will cause the screen to scroll to the top of
    // the page. Please see ``react-scroll`` for more information:
    // https://github.com/fisshy/react-scroll
    var scroll = Scroll.animateScroll;
    scroll.scrollToTop();
  }

  function onVideoDeleteDone() {
    console.log("onVideoDeleteDone: Starting...");
    setFetching(false);
  }

  ////
  //// Misc.
  ////

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      window.scrollTo(0, 0); // Start the page at the top of the page.
      fetchDetail();
    }

        return () => { mounted = false; }
    }, [id]);

  // SPECIAL THANKS TO: https://stackoverflow.com/a/9851769
  // Safari 3.0+ "[object HTMLReusableConstructor]"
  let isSafari =
    /constructor/i.test(window.HTMLReusable) ||
    (function (p) {
      return p.toString() === "[object SafariRemoteNotification]";
    })(
      !window["safari"] ||
        (typeof safari !== "undefined" && window["safari"].pushNotification)
    );

  ////
  //// Component rendering.
  ////

  if (forceURL !== "") {
    return <Navigate to={forceURL} />;
  }

  return (
    <>
      <div class="container">
        <section class="section">
          {/* Desktop Breadcrumbs */}
          <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
            <ul>
              <li>
                <Link to="/dashboard">
                  <FontAwesomeIcon className="fas" icon={faTachometer} />
                  &nbsp;Dashboard
                </Link>
              </li>
              <li class="">
                <Link to="/cms-videos" aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faVideo} />
                  &nbsp;Videos
                </Link>
              </li>
              <li class="is-active">
                <Link aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faEye} />
                  &nbsp;Details
                </Link>
              </li>
            </ul>
          </nav>

          {/* Mobile Breadcrumbs */}
          <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
            <ul>
              <li class="">
                <Link to={`/cms-videos`} aria-current="page">
                  <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                  &nbsp;Back to Videos
                </Link>
              </li>
            </ul>
          </nav>

          {/* Modal(s) */}
          <nav class="">
            <div
              class={`modal ${
                showDeletionConfirmationModal ? "is-active" : ""
              }`}
            >
              <div class="modal-background"></div>
              <div class="modal-card">
                <header class="modal-card-head">
                  <p class="modal-card-title">Are you sure?</p>
                  <button
                    class="delete"
                    aria-label="close"
                    onClick={(e, ses) =>
                      setShowDeletionConfirmationModal(false)
                    }
                  ></button>
                </header>
                <section class="modal-card-body">
                  You are about to delete this video and all the data inside of
                  it. This action is cannot be undone. Are you sure you would
                  like to continue?
                </section>
                <footer class="modal-card-foot">
                  <button
                    class="button is-success"
                    onClick={onDeleteConfirmButtonClick}
                  >
                    Confirm
                  </button>
                  <button
                    class="button"
                    onClick={(e, ses) =>
                      setShowDeletionConfirmationModal(false)
                    }
                  >
                    Cancel
                  </button>
                </footer>
              </div>
            </div>
          </nav>

          {/* Page */}
          <nav class="box">
            <div class="columns">
              <div class="column">
                <h1 class="title is-4">
                  <FontAwesomeIcon className="fas" icon={faVideo} />
                  &nbsp;Video
                </h1>
              </div>
              {!isFetching && (
                <div class="column has-text-right">
                  <button
                    class="button is-small is-info is-fullwidth-mobile"
                    onClick={fetchDetail}
                  >
                    <FontAwesomeIcon className="mdi" icon={faRefresh} />
                    <span class="is-hidden-desktop is-hidden-tablet">
                      &nbsp;Refresh
                    </span>
                  </button>
                  &nbsp;
                  <Link
                    to={`/cms-video/${id}/edit`}
                    class="button is-small is-warning is-fullwidth-mobile"
                    type="button"
                  >
                    <FontAwesomeIcon className="mdi" icon={faPencil} />
                    &nbsp;Edit Video
                  </Link>
                  &nbsp;
                  <button
                    onClick={(e, ses) => setShowDeletionConfirmationModal(true)}
                    class="button is-small is-danger is-fullwidth-mobile"
                    type="button"
                  >
                    <FontAwesomeIcon className="mdi" icon={faTrashCan} />
                    &nbsp;Delete User
                  </button>
                </div>
              )}
            </div>

            <FormErrorBox errors={errors} />

            {isFetching ? (
              <>
                <PageLoadingContent displayMessage={"Loading..."} />
              </>
            ) : (
              <>
                {video && (
                  <>
                    {video.state === 1 && (
                      <article class="message is-info">
                        <div class="message-body">
                          <FontAwesomeIcon className="fas" icon={faGears} />
                          &nbsp;Video is currently queued to be processed.
                        </div>
                      </article>
                    )}
                    {video.state === 2 && (
                      <article class="message is-info">
                        <div class="message-body">
                          <FontAwesomeIcon className="fas" icon={faGears} />
                          &nbsp;Video is currently being processed.
                        </div>
                      </article>
                    )}
                  </>
                )}

                <p class="subtitle is-6">Details</p>

                {!isFetching && video && (
                  <div class="container">
                    <FormInputField
                      label="Name"
                      name="name"
                      placeholder="Text input"
                      value={video.name}
                      helpText=""
                      isRequired={true}
                      maxWidth="150px"
                      disabled={true}
                    />

                    <FormInputField
                      label="Description"
                      name="description"
                      type="text"
                      placeholder="Text input"
                      value={video.description}
                      helpText=""
                      isRequired={true}
                      maxWidth="485px"
                      disabled={true}
                    />

                    <FormSelectField
                      label="State"
                      name="state"
                      placeholder="Pick"
                      selectedValue={video.state}
                      errorText={errors && errors.state}
                      helpText=""
                      options={CMS_VIDEO_STATE_OPTIONS}
                      disabled={true}
                    />

                    {/*
                                        DEVELOPERS NOTES:
                                        - M3U8 format was invented by Apple and is best supported by their tech, as a result the safari browser supports the video format natively.
                                        - Google Chrome doesn't support it natively.
                                        - Therefore we will have two GUIs here:
                                            - If Safari, show video in-browser
                                            - Else any other browser then we make then click to open in browser tab.
                                    */}
                    <div class="field pb-4">
                      <label class="label">Video Content</label>
                      {video.m3u8PublicObjectUrl ? (
                        <>
                          {!isSafari ? (
                            <>
                              <article class="message is-warning pt-0">
                                <div class="message-body">
                                  Your web browser may not support the{" "}
                                  <i>m3u8 file format</i> so you'll need to
                                  download an accompanying plugin to view the
                                  video content. Here is the recommended plugin
                                  for Chrome Web browser:
                                  <br />
                                  <br />-{" "}
                                  <b>
                                    <a href="https://chrome.google.com/webstore/detail/native-hls-playback/emnphkkblegpebimobpbekeedfgemhof">
                                      Native HLS Playback&nbsp;
                                      <FontAwesomeIcon
                                        className="fas"
                                        icon={faArrowUpRightFromSquare}
                                      />
                                    </a>
                                  </b>
                                </div>
                              </article>
                              <div
                                className="has-background-black box has-text-white has-text-centered is-size-3"
                                style={{ borderRadius: "20px" }}
                              >
                                <br />
                                View video by{" "}
                                <b>
                                  <a
                                    href={video.m3u8PublicObjectUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    clicking here&nbsp;
                                    <FontAwesomeIcon
                                      className="fas"
                                      icon={faArrowUpRightFromSquare}
                                    />
                                  </a>
                                </b>{" "}
                                to open in new tab. <br />
                                <br />
                              </div>
                            </>
                          ) : (
                            <>
                              <div
                                className="p-3 has-background-black box"
                                style={{ borderRadius: "20px" }}
                              >
                                <video width="100%" height="100%" controls>
                                  <source
                                    src={video.m3u8PublicObjectUrl}
                                    type="application/x-mpegURL"
                                  />
                                </video>
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <p>Not ready</p>
                      )}
                    </div>

                    <br />
                    <br />

                    <div class="columns">
                      <div class="column is-half">
                        <Link
                          to={`/cms-videos`}
                          class="button is-fullwidth-mobile"
                        >
                          <FontAwesomeIcon className="fas" icon={faArrowLeft} />
                          &nbsp;Back
                        </Link>
                      </div>
                      <div class="column is-half has-text-right">
                        <Link
                          to={`/cms-video/${video.id}/edit`}
                          class="button is-warning is-fullwidth-mobile"
                        >
                          <FontAwesomeIcon className="fas" icon={faPencil} />
                          &nbsp;Edit
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </nav>
        </section>
      </div>
    </>
  );
}

export default VideoDetail;
